import BaseViewerController from "./base_viewer_controller"
import * as pdfjsLib from 'pdfjs-dist'
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`

const PAGE_MARGIN = 16
const CONTAINER_HORIZONTAL_MARGIN = 0
const ROTATE_COUNTER_CLOCKWISE = true

export default class extends BaseViewerController {
  static targets = ["zoomDisplay", "canvas", "pageCounter"]
  static values = {
    url: String,
    renderInProgress: { type: Boolean, default: false }
  }

  connect() {
    super.connect()
    this.pdfDocument = null
    this.canvases = []
    this.overlays = new Map()
    this.currentZoom = 1.0
    this.currentPage = 1
    this.currentRotation = 0
    this.initializeViewer()
    this.setupScrollListener()
  }

  disconnect() {
    this.cleanupScrollListener()
    super.disconnect()
  }

  get element() {
    return this.hasCanvasTarget ? this.canvasTarget : super.element
  }

  get totalPages() {
    return this.pdfDocument ? this.pdfDocument.numPages : 0
  }

  async initializeViewer() {
    await this.loadPDF(this.urlValue)
  }

  destroyViewer() {
    this.cleanupPDF()
  }

  setupScrollListener() {
    this.boundHandleScroll = this.handleScroll.bind(this)
    this.element.addEventListener('scroll', this.boundHandleScroll)
  }

  cleanupScrollListener() {
    if (this.boundHandleScroll) {
      this.element.removeEventListener('scroll', this.boundHandleScroll)
    }
  }

  handleScroll() {
    if (!this.canvases.length) return

    const scrollTop = this.element.scrollTop
    const containerMidpoint = scrollTop + (this.element.clientHeight / 2)

    let currentPageNum = 1
    let accumulatedHeight = 0

    for (let i = 0; i < this.canvases.length; i++) {
      const canvas = this.canvases[i]
      if (!canvas) continue

      const canvasHeight = canvas.clientHeight
      const canvasTop = accumulatedHeight
      const canvasBottom = accumulatedHeight + canvasHeight

      if (containerMidpoint >= canvasTop && containerMidpoint < canvasBottom) {
        currentPageNum = i + 1
        break
      }

      accumulatedHeight += canvasHeight + PAGE_MARGIN
    }

    if (this.currentPage !== currentPageNum) {
      this.currentPage = currentPageNum
      this.updatePageCounter()
    }
  }

  zoomIn() {
    const newZoom = this.currentZoom + this.zoomStepValue
    if (newZoom <= this.maxZoomValue) {
      this.currentZoom = newZoom
      this.applyZoom()
    }
  }

  zoomOut() {
    const newZoom = this.currentZoom - this.zoomStepValue
    if (newZoom >= this.minZoomValue) {
      this.currentZoom = newZoom
      this.applyZoom()
    }
  }

  rotate() {
    const rotationDelta = ROTATE_COUNTER_CLOCKWISE ? -90 : 90
    this.currentRotation = (this.currentRotation + rotationDelta + 360) % 360
    this.renderAllPages()
  }

  applyZoom() {
    // Debounce rapid zoom operations
    if (this.zoomTimeout) {
      clearTimeout(this.zoomTimeout)
    }

    this.zoomTimeout = setTimeout(() => {
      this.renderAllPages()
    }, 150)
  }

  async loadPDF(pdfUrl) {
    try {
      const pdf = await pdfjsLib.getDocument({
        url: pdfUrl,
        wasmUrl: `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/wasm/`
      }).promise

      this.pdfDocument = pdf
      await this.renderAllPages()
    } catch (error) {
      console.error('Error loading PDF:', error)
      this.handleError('Failed to load PDF document')
    }
  }

  async renderAllPages() {
    if (!this.pdfDocument || this.renderInProgressValue) return

    this.renderInProgressValue = true
    this.hideError()

    const outputScale = window.devicePixelRatio || 1

    try {
      // Build render promises for parallel execution
      const renderPromises = []

      for (let pageNum = 1; pageNum <= this.pdfDocument.numPages; pageNum++) {
        renderPromises.push(this.renderPage(pageNum, outputScale))
      }

      // Render all pages in parallel
      await Promise.all(renderPromises)

      // Add UI updates
      this.updateZoomDisplay()
      this.updatePageCounter()

    } catch (error) {
      console.error('Error rendering PDF pages:', error)
      this.handleError('Failed to render PDF pages')
    } finally {
      this.renderInProgressValue = false
    }
  }

  async renderPage(pageNum, outputScale) {
    try {
      const page = await this.pdfDocument.getPage(pageNum)
      const viewport = this.calculateViewport(page)

      await this.renderPageToCanvas(page, viewport, pageNum, outputScale)
    } catch (error) {
      console.error(`Error rendering page ${pageNum}:`, error)
    }
  }

  calculateViewport(page) {
    const containerWidth = this.element.clientWidth
    const availableWidth = containerWidth - CONTAINER_HORIZONTAL_MARGIN
    const pageViewport = page.getViewport({ scale: 1.0, rotation: this.currentRotation })
    const fitScale = availableWidth / pageViewport.width

    // Apply current zoom relative to fit-to-width scale
    return page.getViewport({ scale: fitScale * this.currentZoom, rotation: this.currentRotation })
  }

  async renderPageToCanvas(page, viewport, pageNum, outputScale) {
    // Use cached canvas or create new one
    let canvas = this.canvases[pageNum - 1]
    if (!canvas) {
      canvas = this.createCanvas()
      this.canvases[pageNum - 1] = canvas
    }

    this.updateCanvasDimensions(canvas, viewport, outputScale)

    const context = canvas.getContext('2d')

    // Apply high-DPI scaling
    const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null

    const renderContext = {
      canvasContext: context,
      viewport: viewport,
      transform: transform
    }

    const renderTask = page.render(renderContext)
    await renderTask.promise
  }

  createCanvas() {
    const canvas = document.createElement('canvas')
    canvas.className = 'pdf-page shadow-sm mx-auto mb-4'
    canvas.style.display = 'block'
    this.element.appendChild(canvas)
    return canvas
  }

  updateCanvasDimensions(canvas, viewport, outputScale) {
    canvas.width = Math.floor(viewport.width * outputScale)
    canvas.height = Math.floor(viewport.height * outputScale)
    canvas.style.width = `${Math.floor(viewport.width)}px`
    canvas.style.height = `${Math.floor(viewport.height)}px`
  }

  updateZoomDisplay() {
    if (this.hasZoomDisplayTarget) {
      const zoomPercent = Math.round(this.currentZoom * 100)
      this.zoomDisplayTarget.textContent = `${zoomPercent}%`
    }
  }

  updatePageCounter() {
    if (this.hasPageCounterTarget) {
      this.pageCounterTarget.textContent = `${this.currentPage} / ${this.totalPages}`
    }
  }

  cleanupPDF() {
    try {
      if (this.zoomTimeout) {
        clearTimeout(this.zoomTimeout)
        this.zoomTimeout = null
      }

      if (this.pdfDocument) {
        this.pdfDocument.destroy()
        this.pdfDocument = null
      }

      this.currentZoom = 1.0
      this.renderInProgressValue = false
      this.canvases = []
      this.overlays.clear()
      this.element.innerHTML = ''
    } catch (error) {
      console.error('Error during cleanup:', error)
    }
  }

  cleanup() {
    super.cleanup()
    this.cleanupPDF()
  }

  showOverlay(vizId, pageNum, { url, boundingBox } = {}) {
    this.hideOverlay(vizId)

    const canvas = this.canvases[pageNum - 1]
    if (!canvas) return

    const wrapper = this.ensureCanvasWrapper(canvas)
    const element = boundingBox ? this.buildBoundingBoxOverlay(boundingBox) : this.buildImageOverlay(url)
    element.dataset.vizId = vizId

    wrapper.appendChild(element)
    this.overlays.set(vizId, { element, page: pageNum })
    if (boundingBox) element.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  buildBoundingBoxOverlay({ left, top, width, height }) {
    const el = document.createElement("div")
    el.className = "absolute pointer-events-none bg-accent-purple/20 border border-accent-purple/80"
    Object.assign(el.style, {
      left: `${left * 100}%`,
      top: `${top * 100}%`,
      width: `${width * 100}%`,
      height: `${height * 100}%`,
    })
    return el
  }

  buildImageOverlay(url) {
    const img = document.createElement("img")
    img.src = url
    img.className = "absolute inset-0 w-full h-full object-contain pointer-events-none"
    return img
  }

  hideOverlay(vizId) {
    const overlay = this.overlays.get(vizId)
    if (overlay) {
      overlay.element.remove()
      this.overlays.delete(vizId)
    }
  }

  ensureCanvasWrapper(canvas) {
    if (canvas.parentElement.classList.contains("pdf-page-wrapper")) {
      return canvas.parentElement
    }

    const wrapper = document.createElement("div")
    wrapper.className = "pdf-page-wrapper relative w-fit mx-auto mb-4"
    canvas.parentElement.insertBefore(wrapper, canvas)
    wrapper.appendChild(canvas)

    canvas.classList.remove("mx-auto", "mb-4")

    return wrapper
  }
}
